import SettingsLayout from "../components/SettingsLayout";
import PlanDetails from "./PlanDetails/PlanDetails";
import PaymentMethods from "./PlanDetails/components/PaymentMethods/PaymentMethods";
import BillingHistory from "./PlanDetails/components/BillingHistory/BillingHistory";
import SmsCredits from "./PlanDetails/components/SmsCredits/SmsCredits";
import { useEffect, useState } from "react";
import {
  getInvoicesCount,
  getInvoicesData,
  setDefaultCard,
} from "./PlanService";
import {
  handleErrorMessage,
  initialPageFilterState,
  openSuccessNotification,
} from "helpers/methods";
import { pageFilters } from "models/interfaces";
import {
  convertDataToTableFormat,
  convertCardTableData,
  InvoiceRow,
  PaymentCardRow,
} from "./components/MyPlanHelper";
import { deleteCard, getCards } from "Services/CommonServices";
import history from "utils/history";
import UpgardePlan from "./PlanDetails/components/UpgardePlan/UpgardePlan";
import { getCurrentPlanAndBillingData } from "./MyPlanFunctions";
import AddPaymentMethodDialog from "./components/AddPaymentMethodDialog";
import urls from "global/constants/UrlConstants";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { isTrial } from "redux/authSlice";
import { useAppSelector } from "hooks/use-app-select";
import { selectScreen } from "redux/appSlice";
import { SubscriptionState } from "types/perSeat";
import useSmsCredits from "hooks/use-sms-credits";
import { selectAccount } from "redux/authSlice";
import { fetchAccountDetails } from "screens/Settings/MyAccount/MyAccountServices";
import AwsMarketplaceBilling from "./PlanDetails/components/AwsMarketplaceBilling/AwsMarketplaceBilling";
import usePromoCampaign from "hooks/use-promo-campaign";
import { PlanPromoBanner } from "./components/PlanPromo";

const MyPlan = () => {
  useTitle(title.PLAN);
  // Owned here rather than inside the card so a purchase can refresh the
  // balance without remounting the list under it.
  const smsCredits = useSmsCredits();
  const promise = loadStripe(urls.STRIPE_PUBLIC_KEY);
  const isTrialAccount = useAppSelector(isTrial);
  const redirectedState: any = history.location?.state;
  const [billingDetails, setBillingDetails] = useState<InvoiceRow[]>([]);
  const [count, setCount] = useState(0);
  const [cardsLoader, setCardsLoader] = useState<boolean>(false);
  const [loaderBilling, setLoaderBilling] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [cardAsPrimaryModal, setCardAsPrimaryModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [cardId, setCardId] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentCardRow[]>([]);

  const [billingFilters, setBillingFilters] = useState<pageFilters>(
    initialPageFilterState("", 1, 5),
  );
  // Seasonal offer for whoever can buy — see `usePromoCampaign`.
  const { campaign: promoCampaign } = usePromoCampaign();
  const [upgradePlanScreen, setUpgradePlanScreen] = useState<boolean>(
    redirectedState?.upgradePlan ? redirectedState.upgradePlan : false,
  );
  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [cardData, setCardData] = useState<
    {
      id: string;
      number: number;
      default: boolean;
    }[]
  >([]);
  const [accountStats, setAccountStats] = useState<any>({});
  const [previousSelected, setPreviousSelected] = useState<string>("");
  const [paymentID, setPaymentID] = useState<string>("");
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);
  const [subscriptionState, setSubscriptionState] =
    useState<SubscriptionState | null>(null);
  const [openAddPaymentMethodDialog, setOpenAddPaymentMethodDialog] =
    useState<boolean>(false);
  const [doesNewPaymentMethodAdded, setDoesNewPaymentMethodAdded] =
    useState<boolean>(false);
  const isMobile = useAppSelector(selectScreen);
  const accountName = useAppSelector(selectAccount);
  /**
   * ACCOUNT.BILLING_SOURCE: undefined while it loads, null if it could not be
   * read. An AWS Marketplace account gets no Stripe purchase anywhere on this
   * screen (core would refuse it with a 409); until the source is known the
   * plan card stays on its skeleton so the Stripe actions never flash in. A
   * failed read falls back to the normal screen, where core still guards.
   */
  const [billingSource, setBillingSource] = useState<string | null | undefined>(
    undefined,
  );
  const isBillingSourceLoading = billingSource === undefined;
  const isAwsMarketplaceBilled =
    billingSource === strings.AWS_MARKETPLACE_BILLING_SOURCE;

  /**
   * Last four of the card an SMS credit purchase would actually charge — the
   * default one, or the first usable card when no default is set, matching how
   * `fetchPaymentMethods` picks `paymentID`. Undefined until the cards load, in
   * which case the buy panel drops the "ending 1234" clause rather than naming
   * a card that might not be the one charged.
   */
  const defaultCardLast4 = (() => {
    const card =
      paymentMethods.find((row) => row.isDefault && !row.isExpired) ??
      paymentMethods.find((row) => !row.isExpired);
    const digits = String(card?.number ?? "").replace(/\D/g, "");
    return digits.length >= 4 ? digits.slice(-4) : undefined;
  })();

  useEffect(() => {
    fetchPaymentMethods();
    // Owned here (not in PlanDetails) so the plan + account stats are available
    // when the screen opens straight into the upgrade view.
    getUserData();
    fetchAccountDetails(strings.inkfree, accountName)
      .then((account: any) => setBillingSource(account?.billingSource ?? null))
      .catch(() => setBillingSource(null));
  }, []);

  useEffect(() => {
    fetchData();
  }, [billingFilters]);

  /**
   * Plan card/account-stats and billing history are fetched side by side, each
   * driven by its own loading flag (`loader`, `loaderBilling`) — one running
   * long must not hold the other's skeleton on, or its data back from showing
   * the moment it lands.
   */
  const getUserData = () => {
    return Promise.all([
      getCurrentPlanAndBillingData(
        setLoader,
        setCardData,
        setIsSubscriptionActive,
        setAccountStats,
        setCurrentPlan,
        setPreviousSelected,
        setPaymentID,
        setSubscriptionState,
      ),
      fetchData(),
    ]);
  };

  const setDefaultCardHandler = async () => {
    try {
      setCardsLoader(true);
      setCardAsPrimaryModal(false);
      const res = await setDefaultCard(paymentID);
      openSuccessNotification(
        res.message ?? "Card set as default successfully",
      );
      await fetchPaymentMethods();
      setCardsLoader(false);
    } catch (error: any) {
      setCardsLoader(false);
      setCardAsPrimaryModal(false);
      handleErrorMessage(error);
    }
  };

  const deleteCardHandler = async (cardId: string) => {
    try {
      setCardsLoader(true);
      setOpenDeleteModal(false);
      const res = await deleteCard(cardId);
      openSuccessNotification(res.message ?? "Delete Card Successfully");
      await fetchPaymentMethods();
      setCardsLoader(false);
    } catch (error: any) {
      setCardsLoader(false);
      handleErrorMessage(error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      setCardsLoader(true);
      const paymentMethodsRes = await getCards();
      const cardsWithExpiryStatus = paymentMethodsRes.map((card: any) => ({
        ...card,
        isExpired: isCardExpired(card.expirationDate),
      }));

      const validDefaultCard = cardsWithExpiryStatus.find(
        (card: any) => card.default && !card.isExpired,
      );

      if (!validDefaultCard) {
        const firstValidCard = cardsWithExpiryStatus.find(
          (card: any) => !card.isExpired,
        );
        setPaymentID(firstValidCard?.id || "");
      } else {
        setPaymentID(validDefaultCard?.id);
      }
      setPaymentMethods(convertCardTableData(cardsWithExpiryStatus));
      setCardsLoader(false);
    } catch (error: any) {
      setCardsLoader(false);
      handleErrorMessage(error);
    }
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setBillingFilters({
      ...billingFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setBillingFilters({
      ...billingFilters,
      page: newPage,
    });
  };

  const isCardExpired = (expirationDate: string) => {
    if (!expirationDate) return false;

    const expiry = new Date(expirationDate);
    expiry.setHours(23, 59, 59, 999);

    return new Date() > expiry;
  };

  const fetchData = async () => {
    try {
      setLoaderBilling(true);
      const [data, count] = await Promise.all([
        getInvoicesData(billingFilters.page!, 5),
        getInvoicesCount(),
      ]);
      const tableData = convertDataToTableFormat(data);
      setBillingDetails(tableData);
      setCount(count);
      setLoaderBilling(false);
    } catch (error: any) {
      setLoaderBilling(false);
      handleErrorMessage(error);
    }
  };

  const getAddPaymentMethodDialog = () => {
    return (
      <Elements stripe={promise}>
        <AddPaymentMethodDialog
          isDialogOpen={openAddPaymentMethodDialog}
          setDialogOpen={setOpenAddPaymentMethodDialog}
          doesNewPaymentMethodAdded={doesNewPaymentMethodAdded}
          setDoesNewPaymentMethodAdded={setDoesNewPaymentMethodAdded}
          fetchPaymentMethods={fetchPaymentMethods}
        />
      </Elements>
    );
  };
  return (
    <>
      {upgradePlanScreen &&
      !isBillingSourceLoading &&
      !isAwsMarketplaceBilled ? (
        <UpgardePlan
          currentPlan={currentPlan}
          accountStats={accountStats}
          isSubscriptionActive={isSubscriptionActive}
          cardData={cardData}
          getUserData={getUserData}
          setUpgradePlanScreen={setUpgradePlanScreen}
          setLoading={setLoader}
          loader={loader}
        />
      ) : (
        <SettingsLayout
          activeTab="My Plan"
          title="Plan & Billing"
          contentSpacing={2.5}
        >
          {isAwsMarketplaceBilled ? (
            <AwsMarketplaceBilling
              isLoading={loader}
              currentPlan={currentPlan}
              accountStats={accountStats}
            />
          ) : (
            <>
              {/* Seasonal offer. Waits for the billing source like the Stripe
                  panels below — a Marketplace-billed account can't buy here. */}
              {promoCampaign && !isBillingSourceLoading && (
                <PlanPromoBanner
                  campaign={promoCampaign}
                  onViewOffer={() => {
                    setUpgradePlanScreen(true);
                    window.scrollTo(0, 0);
                  }}
                />
              )}
              <PlanDetails
                isLoading={loader || isBillingSourceLoading}
                setLoading={setLoader}
                getUserData={getUserData}
                currentPlan={currentPlan}
                setUpgradePlanScreen={setUpgradePlanScreen}
                accountStats={accountStats}
                isSubscriptionActive={isSubscriptionActive}
                subscriptionState={subscriptionState}
                isMobile={isMobile}
              />
              {/* The Stripe panels below wait for the billing source too, like
                  the plan card above: an AWS Marketplace account must never
                  see them, not even before the account details load. */}
              {!isBillingSourceLoading && (
                <>
                  {/* Prepaid SMS wallet, directly under the plan card: it is part of
              what the account is paying for, so it reads with the plan rather
              than with the cards. Buying still charges the saved card and a
              purchase still lands in Invoices — both are reachable from inside
              this card, so neither has to come first. Hides itself when the
              plan has no SMS. */}
                  <SmsCredits
                    credits={smsCredits}
                    cardLast4={defaultCardLast4}
                    onManagePaymentMethods={() =>
                      setOpenAddPaymentMethodDialog(true)
                    }
                  />
                  <PaymentMethods
                    isLoading={cardsLoader}
                    selected={paymentID}
                    setSelected={setPaymentID}
                    paymentMethods={paymentMethods}
                    setDefaultCardHandler={setDefaultCardHandler}
                    setCardAsPrimaryModal={setCardAsPrimaryModal}
                    cardAsPrimaryModal={cardAsPrimaryModal}
                    deleteCardHandler={deleteCardHandler}
                    cardId={cardId}
                    setCardId={setCardId}
                    openDeleteModal={openDeleteModal}
                    setOpenDeleteModal={setOpenDeleteModal}
                    setPreviousSelected={setPreviousSelected}
                    previousSelected={previousSelected}
                    canAddPayment={
                      hasAccessTo(
                        strings.resources.billing,
                        strings.addPermission,
                      ) && !isTrialAccount
                    }
                    // This card's own loading, not the plan card's — an in-flight
                    // card list is what "Add Payment Method" needs to wait on.
                    addPaymentDisabled={cardsLoader}
                    onAddPaymentMethod={() =>
                      setOpenAddPaymentMethodDialog(true)
                    }
                  />
                  <BillingHistory
                    BillingDetails={billingDetails}
                    totalCount={count}
                    loading={loaderBilling}
                    billingFilters={billingFilters}
                    perPageRowsChangeHandler={perPageRowsChangeHandler}
                    pageChangeHandler={pageChangeHandler}
                  />
                </>
              )}
            </>
          )}
        </SettingsLayout>
      )}
      {getAddPaymentMethodDialog()}
    </>
  );
};

export default MyPlan;
